import type { Dispatch, ReactNode } from "react";
import React, { createContext, useCallback, useContext, useReducer, useEffect } from "react";
import { CustomFieldEntity } from "@laborchart-modules/common/dist/rethink/schemas/enums/custom-fields";
import { Tearsheet } from "@procore/core-react";
import type { TableApi } from "@procore/data-table";
import { useGetCustomFields, useGetIntegratedFields } from "../../common/queries/queries";
import { CreatePersonTearsheet } from "./create-person-tearsheet";
import { PersonDetailTearsheet } from "./person-detail-tearsheet-content";
import {
   AssignmentTearsheetProvider,
   useAssignmentTearsheet,
} from "../assignment/assignment-tearsheet";
import type { CreateAssignmentAction } from "../assignment/assignment-tearsheet";
import type { FormSelectOption } from "@/react/prop-types";
import { useProjectTearsheet } from "../project/project-tearsheet";

type PersonTearsheetState = OpenState | ClosedState;
type OpenState = { isOpen: true } & (CreatePerson | PersonDetail | CreateAssignment);
type ClosedState = { isOpen: false; show: null };
type CreatePerson = { show: "create-person" };
type PersonDetail = {
   show: "person-detail";
   personId: string;
   showActiveTab?: string;
   callback?: (data?: any) => void;
};
type CreateAssignment = {
   show: "create-assignment";
   people?: Array<FormSelectOption<string>>;
   callback?: (data?: any) => void;
};

type CreatePersonAction = { type: "open-create-person" };
type PersonDetailAction = {
   type: "open-person-detail";
   personId: string;
   showActiveTab?: string;
   callback?: (data: any) => void;
};
type CloseAction = { type: "close" };

export type PersonTearsheetActions =
   | CreatePersonAction
   | PersonDetailAction
   | CloseAction
   | CreateAssignmentAction;

function reducer(
   _state: PersonTearsheetState,
   action: PersonTearsheetActions,
): PersonTearsheetState {
   switch (action.type) {
      case "open-create-person": {
         return {
            isOpen: true,
            show: "create-person",
         };
      }
      case "open-person-detail": {
         return {
            isOpen: true,
            show: "person-detail",
            personId: action.personId,
            showActiveTab: action.showActiveTab,
            callback: action.callback,
         };
      }
      case "open-create-assignment": {
         return {
            isOpen: true,
            show: "create-assignment",
            people: action.people,
            callback: action.callback,
         };
      }
      case "close": {
         return {
            isOpen: false,
            show: null,
         };
      }
   }
}

const initialState: PersonTearsheetState = {
   isOpen: false,
   show: null,
};

const PersonTearsheetContext = createContext<
   (PersonTearsheetState & { dispatch: Dispatch<PersonTearsheetActions> }) | null
>(null);

export function PersonTearsheetProvider({
   children,
   peopleTableApi,
}: Readonly<{
   children: ReactNode;
   peopleTableApi?: TableApi;
}>) {
   const [state, dispatch] = useReducer(reducer, initialState);
   return (
      <PersonTearsheetContext.Provider value={{ ...state, dispatch }}>
         {children}
         <PersonTearsheet peopleTableApi={peopleTableApi} />
      </PersonTearsheetContext.Provider>
   );
}

export function usePersonTearsheet() {
   const context = useContext(PersonTearsheetContext);

   if (!context) {
      throw Error(
         "'usePersonTearsheet' is supposed to be used in the component that is a child of 'PersonTearsheetProvider'.",
      );
   }

   return context;
}

export function PersonTearsheet(props: Readonly<{ peopleTableApi?: TableApi }>) {
   const state = usePersonTearsheet();
   const isPersonDetailOpen = state.show === "person-detail";
   const isCreatePersonOpen = state.show === "create-person";
   const isCreateAssignmentOpen = state.show === "create-assignment";

   const { data: customFields } = useGetCustomFields(CustomFieldEntity.PERSON);

   const { data: integratedFields } = useGetIntegratedFields();
   const { dispatch: projectTearsheetDispatch } = useProjectTearsheet();

   const handleClose = () => {
      if (isPersonDetailOpen) {
         state.callback?.();
      }
      // Refresh people table when closing from create-assignment flow
      if (isCreateAssignmentOpen) {
         props.peopleTableApi?.refreshServerSide({ purge: true });
      }
      // Don't dispatch close if we're closing from create-assignment flow
      // because the callback has already dispatched open-person-detail
      if (!isCreateAssignmentOpen) {
         state.dispatch({ type: "close" });
      }
   };

   const onUpdatePeople = useCallback(() => {
      props.peopleTableApi?.refreshServerSide({ purge: true });
   }, [props.peopleTableApi]);

   const onPersonCreated = (personId: string) => {
      state.dispatch({ type: "open-person-detail", personId });
      props.peopleTableApi?.refreshServerSide({});
   };

   // Wrapper component that automatically opens create-assignment when mounted

   const AssignmentFlowWrapper = () => {
      const { dispatch } = useAssignmentTearsheet();
      const isCreateAssignment = state.isOpen && state.show === "create-assignment";

      useEffect(() => {
         dispatch({
            type: "open-create-assignment",
            people: isCreateAssignment ? state.people : undefined,
            callback: isCreateAssignment ? state.callback : undefined,
            onClose: handleClose,
         });
      }, [dispatch]);

      return null;
   };

   // When assignment flow is active, use the AssignmentTearsheetProvider
   // which will handle its own tearsheet rendering
   if (isCreateAssignmentOpen) {
      return (
         <AssignmentTearsheetProvider assignmentTableApi={undefined} onClose={handleClose}>
            <AssignmentFlowWrapper />
         </AssignmentTearsheetProvider>
      );
   }

   return (
      <Tearsheet open={state.isOpen} onClose={handleClose}>
         {isCreatePersonOpen && (
            <CreatePersonTearsheet onPersonCreated={onPersonCreated} onClose={handleClose} />
         )}
         {isPersonDetailOpen && (
            <PersonDetailTearsheet
               personId={(state as any).personId}
               customFields={customFields!}
               integratedFields={integratedFields!}
               onUpdatePeople={onUpdatePeople}
               showActiveTab={(state as any).showActiveTab}
               onClose={handleClose}
               projectTearsheetDispatch={projectTearsheetDispatch}
            />
         )}
      </Tearsheet>
   );
}

export function onCreatePersonClick(dispatch: Dispatch<PersonTearsheetActions>) {
   dispatch({ type: "open-create-person" });
}

export function onPersonDetailClick(
   dispatch: Dispatch<PersonTearsheetActions>,
   id: string,
   showActiveTab?: string,
   callback?: () => void,
) {
   dispatch({ type: "open-person-detail", personId: id, showActiveTab, callback });
}
